import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue 
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { 
  ArrowLeft,
  Plus, 
  Search, 
  Users,
  Loader2,
  Mail,
  Phone,
  Globe,
  Building2,
  FolderKanban,
  Pencil,
  Calendar as CalendarIcon,
  MessageSquare
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusOptions = [
  { value: 'not_started', label: 'Not Started' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' }
];

const projectTypes = [
  { value: 'web_design', label: 'Web Design' },
  { value: 'seo', label: 'SEO' },
  { value: 'branding', label: 'Branding' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'software_development', label: 'Software Development' },
  { value: 'other', label: 'Other' }
];

// Project type specific field templates
const projectTemplates = {
  web_design: {
    fields: [
      { name: 'num_pages', label: 'Number of Pages', type: 'number', placeholder: 'e.g., 5' },
      { name: 'cms_platform', label: 'CMS Platform', type: 'select', options: ['WordPress', 'Webflow', 'Shopify', 'Custom', 'None'] },
      { name: 'responsive', label: 'Responsive Design', type: 'boolean', default: true },
      { name: 'design_style', label: 'Design Style', type: 'select', options: ['Modern', 'Minimalist', 'Corporate', 'Creative', 'E-commerce'] },
      { name: 'has_ecommerce', label: 'E-commerce Features', type: 'boolean', default: false },
      { name: 'hosting_provider', label: 'Hosting Provider', type: 'text', placeholder: 'e.g., AWS, GoDaddy' },
    ]
  },
  seo: {
    fields: [
      { name: 'target_keywords', label: 'Target Keywords', type: 'textarea', placeholder: 'Enter primary keywords (comma separated)' },
      { name: 'competitor_urls', label: 'Competitor URLs', type: 'textarea', placeholder: 'Enter competitor URLs (one per line)' },
      { name: 'monthly_traffic_goal', label: 'Monthly Traffic Goal', type: 'number', placeholder: 'e.g., 10000' },
      { name: 'current_ranking', label: 'Current Google Ranking', type: 'text', placeholder: 'e.g., Page 2-3' },
      { name: 'local_seo', label: 'Local SEO Focus', type: 'boolean', default: false },
      { name: 'content_strategy', label: 'Content Strategy', type: 'select', options: ['Blog Posts', 'Landing Pages', 'Product Pages', 'All'] },
    ]
  },
  software_development: {
    fields: [
      { name: 'tech_stack', label: 'Tech Stack', type: 'text', placeholder: 'e.g., React, Node.js, PostgreSQL' },
      { name: 'repository_url', label: 'Repository URL', type: 'text', placeholder: 'https://github.com/...' },
      { name: 'deployment_env', label: 'Deployment Environment', type: 'select', options: ['AWS', 'Azure', 'GCP', 'Heroku', 'Vercel', 'Self-hosted'] },
      { name: 'sprint_duration', label: 'Sprint Duration (weeks)', type: 'select', options: ['1', '2', '3', '4'] },
      { name: 'has_api', label: 'API Development', type: 'boolean', default: true },
      { name: 'testing_required', label: 'Testing Requirements', type: 'select', options: ['Unit Tests', 'Integration Tests', 'E2E Tests', 'All', 'None'] },
    ]
  },
  branding: {
    fields: [
      { name: 'brand_type', label: 'Brand Type', type: 'select', options: ['New Brand', 'Rebrand', 'Brand Refresh', 'Brand Extension'] },
      { name: 'deliverables', label: 'Deliverables', type: 'textarea', placeholder: 'Logo, Business Cards, Brand Guidelines...' },
      { name: 'industry', label: 'Industry', type: 'text', placeholder: 'e.g., Technology, Healthcare' },
      { name: 'target_audience', label: 'Target Audience', type: 'text', placeholder: 'e.g., Young professionals 25-40' },
      { name: 'color_preferences', label: 'Color Preferences', type: 'text', placeholder: 'e.g., Blue, Professional' },
      { name: 'competitors_ref', label: 'Competitor References', type: 'textarea', placeholder: 'Brands to reference or avoid' },
    ]
  },
  marketing: {
    fields: [
      { name: 'campaign_type', label: 'Campaign Type', type: 'select', options: ['Social Media', 'Email', 'PPC', 'Content Marketing', 'Full Funnel'] },
      { name: 'platforms', label: 'Platforms', type: 'text', placeholder: 'e.g., Facebook, Instagram, Google Ads' },
      { name: 'monthly_budget', label: 'Monthly Ad Budget ($)', type: 'number', placeholder: 'e.g., 5000' },
      { name: 'target_cpa', label: 'Target CPA ($)', type: 'number', placeholder: 'e.g., 50' },
      { name: 'conversion_goal', label: 'Conversion Goal', type: 'select', options: ['Lead Generation', 'Sales', 'App Installs', 'Brand Awareness'] },
      { name: 'reporting_frequency', label: 'Reporting Frequency', type: 'select', options: ['Weekly', 'Bi-weekly', 'Monthly'] },
    ]
  },
  other: {
    fields: [
      { name: 'project_scope', label: 'Project Scope', type: 'textarea', placeholder: 'Describe the project scope...' },
      { name: 'key_deliverables', label: 'Key Deliverables', type: 'textarea', placeholder: 'List main deliverables...' },
      { name: 'special_requirements', label: 'Special Requirements', type: 'textarea', placeholder: 'Any special requirements...' },
    ]
  }
};

const ClientDetail = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { user, getAuthHeaders } = useAuth();
  const [client, setClient] = useState(null);
  const [projects, setProjects] = useState([]);
  const [communications, setCommunications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditClientOpen, setIsEditClientOpen] = useState(false);
  const [isCommOpen, setIsCommOpen] = useState(false);
  const [dueDate, setDueDate] = useState(null);
  const [clientForm, setClientForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    website: '',
    notes: ''
  });
  const [projectForm, setProjectForm] = useState({
    name: '',
    description: '',
    project_type: 'web_design',
    budget: '',
    custom_fields: {}
  });
  const [commForm, setCommForm] = useState({
    subject: '',
    message: '',
    communication_type: 'note'
  });

  useEffect(() => {
    fetchData();
  }, [clientId]);

  // Initialize custom fields when project type changes
  useEffect(() => {
    const template = projectTemplates[projectForm.project_type];
    if (template) {
      const defaultFields = {};
      template.fields.forEach(field => {
        if (field.default !== undefined) {
          defaultFields[field.name] = field.default;
        } else if (field.type === 'boolean') {
          defaultFields[field.name] = false;
        } else {
          defaultFields[field.name] = '';
        }
      });
      setProjectForm(prev => ({ ...prev, custom_fields: defaultFields }));
    }
  }, [projectForm.project_type]);

  const fetchData = async () => {
    try {
      const [clientRes, projectsRes, commsRes] = await Promise.all([
        axios.get(`${API_URL}/clients/${clientId}`, getAuthHeaders()),
        axios.get(`${API_URL}/projects?client_id=${clientId}`, getAuthHeaders()),
        axios.get(`${API_URL}/communications?client_id=${clientId}`, getAuthHeaders())
      ]);
      setClient(clientRes.data);
      setProjects(projectsRes.data);
      setCommunications(commsRes.data);
      setClientForm({
        name: clientRes.data.name,
        email: clientRes.data.email || '',
        phone: clientRes.data.phone || '',
        company: clientRes.data.company || '',
        website: clientRes.data.website || '',
        notes: clientRes.data.notes || '',
        organization_id: clientRes.data.organization_id
      });
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load client data');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateClient = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API_URL}/clients/${clientId}`, clientForm, getAuthHeaders());
      toast.success('Client updated successfully');
      setIsEditClientOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update client');
    }
  };

  // Update custom field value
  const updateCustomField = (fieldName, value) => {
    setProjectForm(prev => ({
      ...prev,
      custom_fields: {
        ...prev.custom_fields,
        [fieldName]: value
      }
    }));
  };

  // Render custom fields based on project type
  const renderCustomFields = () => {
    const template = projectTemplates[projectForm.project_type];
    if (!template) return null;

    const typeInfo = projectTypes.find(t => t.value === projectForm.project_type);

    return (
      <div className="space-y-4 pt-4 border-t border-slate-100">
        <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
          {typeInfo?.label || 'Project'} Details
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {template.fields.map(field => (
            <div key={field.name} className={field.type === 'textarea' ? 'md:col-span-2' : ''}>
              {field.type === 'boolean' ? (
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <Label htmlFor={`create-${field.name}`} className="cursor-pointer">{field.label}</Label>
                  <Switch
                    id={`create-${field.name}`}
                    checked={projectForm.custom_fields?.[field.name] || false}
                    onCheckedChange={(checked) => updateCustomField(field.name, checked)}
                  />
                </div>
              ) : field.type === 'select' ? (
                <div className="space-y-2">
                  <Label>{field.label}</Label>
                  <Select
                    value={projectForm.custom_fields?.[field.name] || ''}
                    onValueChange={(value) => updateCustomField(field.name, value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={`Select ${field.label.toLowerCase()}`} />
                    </SelectTrigger>
                    <SelectContent>
                      {field.options.map(opt => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : field.type === 'textarea' ? (
                <div className="space-y-2">
                  <Label>{field.label}</Label>
                  <Textarea
                    value={projectForm.custom_fields?.[field.name] || ''}
                    onChange={(e) => updateCustomField(field.name, e.target.value)}
                    placeholder={field.placeholder}
                    rows={2}
                  />
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>{field.label}</Label>
                  <Input
                    type={field.type}
                    value={projectForm.custom_fields?.[field.name] || ''}
                    onChange={(e) => updateCustomField(field.name, e.target.value)}
                    placeholder={field.placeholder}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/projects`, {
        ...projectForm,
        client_id: clientId,
        organization_id: client.organization_id,
        due_date: dueDate ? format(dueDate, 'yyyy-MM-dd') : null,
        budget: projectForm.budget ? parseFloat(projectForm.budget) : null,
        custom_fields: projectForm.custom_fields
      }, getAuthHeaders());
      toast.success('Project created successfully');
      setIsCreateOpen(false);
      setProjectForm({ name: '', description: '', project_type: 'web_design', budget: '', custom_fields: {} });
      setDueDate(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create project');
    }
  };

  const handleAddCommunication = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/communications`, {
        ...commForm,
        client_id: clientId
      }, getAuthHeaders());
      toast.success('Communication added');
      setCommForm({ subject: '', message: '', communication_type: 'note' });
      fetchData();
    } catch (error) {
      toast.error('Failed to add communication');
    }
  };

  const filteredProjects = projects.filter(project => 
    project.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!client) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-slate-700">Client not found</h2>
        <Button variant="link" onClick={() => navigate('/clients')}>Back to Clients</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="client-detail-page">
      {/* Back button and Header */}
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={() => navigate(-1)}
          data-testid="back-btn"
        >
          <ArrowLeft size={20} />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            {client.name}
          </h1>
          <p className="text-slate-500">{client.company || 'Client'}</p>
        </div>
        <Button 
          variant="outline" 
          onClick={() => setIsEditClientOpen(true)}
          data-testid="edit-client-btn"
        >
          <Pencil size={16} className="mr-2" />
          Edit Client
        </Button>
      </div>

      {/* Client Info Card */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-start gap-6">
            <div className="w-16 h-16 rounded-xl bg-blue-50 flex items-center justify-center">
              <Users size={32} className="text-blue-600" />
            </div>
            <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-slate-500">Email</p>
                <p className="font-medium text-slate-900">{client.email || 'Not set'}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Phone</p>
                <p className="font-medium text-slate-900">{client.phone || 'Not set'}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Website</p>
                {client.website ? (
                  <a href={client.website} target="_blank" rel="noopener noreferrer" className="font-medium text-blue-600 hover:underline">
                    {client.website.replace(/^https?:\/\//, '')}
                  </a>
                ) : (
                  <p className="font-medium text-slate-900">Not set</p>
                )}
              </div>
              <div>
                <p className="text-sm text-slate-500">Created</p>
                <p className="font-medium text-slate-900">{format(new Date(client.created_at), 'MMM d, yyyy')}</p>
              </div>
            </div>
          </div>
          {client.notes && (
            <div className="mt-4 pt-4 border-t border-slate-100">
              <p className="text-sm text-slate-500 mb-1">Notes</p>
              <p className="text-slate-700">{client.notes}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stats and Communications Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-emerald-50 flex items-center justify-center">
                <FolderKanban size={24} className="text-emerald-600" />
              </div>
              <div>
                <p className="text-3xl font-bold text-slate-900">{projects.length}</p>
                <p className="text-sm text-slate-500">Total Projects</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm lg:col-span-2">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
              <MessageSquare size={18} className="text-slate-500" />
              Communications
            </CardTitle>
            <Button variant="outline" size="sm" onClick={() => setIsCommOpen(true)}>
              <Plus size={14} className="mr-1" />
              Add Note
            </Button>
          </CardHeader>
          <CardContent className="pt-2">
            {communications.length > 0 ? (
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {communications.slice(0, 3).map((comm) => (
                  <div key={comm.id} className="text-sm bg-slate-50 rounded p-2">
                    <div className="flex justify-between items-start">
                      <span className="font-medium text-slate-700">{comm.subject}</span>
                      <span className="text-xs text-slate-400">{format(new Date(comm.created_at), 'MMM d')}</span>
                    </div>
                    <p className="text-slate-500 text-xs mt-1 line-clamp-1">{comm.message}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-400">No communications yet</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Projects Section */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h2 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
          Projects
        </h2>
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 hover:bg-slate-800" data-testid="add-project-btn">
              <Plus size={18} className="mr-2" />
              New Project
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Plus Jakarta Sans' }}>Create New Project</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateProject} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Project Name *</Label>
                <Input
                  value={projectForm.name}
                  onChange={(e) => setProjectForm({ ...projectForm, name: e.target.value })}
                  placeholder="e.g., Website Redesign"
                  required
                  data-testid="new-project-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={projectForm.description}
                  onChange={(e) => setProjectForm({ ...projectForm, description: e.target.value })}
                  placeholder="Project description..."
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Project Type</Label>
                  <Select
                    value={projectForm.project_type}
                    onValueChange={(value) => setProjectForm({ ...projectForm, project_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {projectTypes.map(type => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Budget ($)</Label>
                  <Input
                    type="number"
                    value={projectForm.budget}
                    onChange={(e) => setProjectForm({ ...projectForm, budget: e.target.value })}
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Due Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <CalendarIcon size={16} className="mr-2" />
                      {dueDate ? format(dueDate, 'PPP') : 'Select date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar mode="single" selected={dueDate} onSelect={setDueDate} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Custom Fields based on Project Type */}
              {renderCustomFields()}

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="save-new-project-btn">
                  Create Project
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-4">
          <div className="relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search projects..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-client-projects-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Projects Grid */}
      {filteredProjects.length === 0 ? (
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-12 text-center">
            <FolderKanban size={48} className="mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-700">No projects yet</h3>
            <p className="text-slate-500 mt-1">Create the first project for this client</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProjects.map((project) => (
            <Card 
              key={project.id} 
              className="border-slate-200 shadow-sm card-hover cursor-pointer"
              onClick={() => navigate(`/projects/${project.id}`)}
              data-testid={`client-project-card-${project.id}`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <CardTitle className="text-lg font-semibold text-slate-900">
                    {project.name}
                  </CardTitle>
                  <span className={`status-badge status-${project.status}`}>
                    {statusOptions.find(s => s.value === project.status)?.label}
                  </span>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600 line-clamp-2 mb-4">
                  {project.description || 'No description'}
                </p>
                <div className="flex items-center justify-between text-xs text-slate-400">
                  <span className="capitalize">{project.project_type.replace('_', ' ')}</span>
                  {project.due_date && (
                    <span className="flex items-center gap-1">
                      <CalendarIcon size={12} />
                      {format(new Date(project.due_date), 'MMM d, yyyy')}
                    </span>
                  )}
                </div>
                {project.budget && (
                  <p className="mt-2 text-sm font-medium text-slate-700">
                    Budget: ${project.budget.toLocaleString()}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Client Modal */}
      <Dialog open={isEditClientOpen} onOpenChange={setIsEditClientOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Plus Jakarta Sans' }}>Edit Client</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateClient} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Client Name *</Label>
              <Input
                value={clientForm.name}
                onChange={(e) => setClientForm({ ...clientForm, name: e.target.value })}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={clientForm.email}
                  onChange={(e) => setClientForm({ ...clientForm, email: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={clientForm.phone}
                  onChange={(e) => setClientForm({ ...clientForm, phone: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Company</Label>
                <Input
                  value={clientForm.company}
                  onChange={(e) => setClientForm({ ...clientForm, company: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Website</Label>
                <Input
                  value={clientForm.website}
                  onChange={(e) => setClientForm({ ...clientForm, website: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={clientForm.notes}
                onChange={(e) => setClientForm({ ...clientForm, notes: e.target.value })}
                rows={3}
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsEditClientOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800">
                Update Client
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Communication Modal */}
      <Dialog open={isCommOpen} onOpenChange={setIsCommOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Plus Jakarta Sans' }}>Add Communication</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddCommunication} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Subject *</Label>
                <Input
                  value={commForm.subject}
                  onChange={(e) => setCommForm({ ...commForm, subject: e.target.value })}
                  placeholder="Subject"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Type</Label>
                <Select
                  value={commForm.communication_type}
                  onValueChange={(value) => setCommForm({ ...commForm, communication_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="note">Note</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="call">Phone Call</SelectItem>
                    <SelectItem value="meeting">Meeting</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Message *</Label>
              <Textarea
                value={commForm.message}
                onChange={(e) => setCommForm({ ...commForm, message: e.target.value })}
                placeholder="Communication details..."
                rows={4}
                required
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsCommOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800">
                Add Communication
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientDetail;
